import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";
import { motion, AnimatePresence } from "framer-motion";

import { useAuth } from "../context/AuthContext";
import api, { showNotification, NOTIFICATIONTYPES } from "../utils/apiClient";
import {
  API_ENDPOINTS, STORAGE_KEYS, ROUTES, VALIDATION,
  ERROR_MESSAGES, SUCCESS_MESSAGES, APP_CONFIG,
} from "../utils/constants";

import {
  User, Pencil, Phone, Mail, CheckCircle, AlertTriangle,
  ShieldCheck, Camera, X, RefreshCw, Smartphone, MapPin,
  CreditCard, Save, Loader2
} from "lucide-react";

import DeleteAccountButton from "../components/profile/DeleteAccountButton";

const Profile = () => {
  const { isAuthenticated, updateUserProfile } = useAuth();
  const navigate = useNavigate();

  const fileInputRef = useRef(null);
  const hasFetchedRef = useRef(false);
  const abortControllerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [formData, setFormData] = useState({
    name: "", address: "", alternateNumber: "", profile: "",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const normalizeUser = (raw) => {
    if (!raw) return null;
    const id = raw.id ?? raw.user_id ?? raw.userid ?? raw.userId ?? null;
    return {
      id, name: raw.name ?? "", phoneNumber: raw.phoneNumber ?? raw.phone ?? "",
      email: raw.email ?? "", address: raw.address ?? "",
      alternateNumber: raw.alternateNumber ?? raw.alt_phone ?? raw.altphone ?? "",
      profile: raw.profile ?? raw.profile_pic ?? raw.profilePic ?? "",
      isDocumentVerified: raw.isDocumentVerified ?? raw.is_document_verified ?? 0,
    };
  };

  useEffect(() => {
    if (!isAuthenticated) { navigate(ROUTES.LOGIN, { replace: true }); return; }
    if (hasFetchedRef.current) return;
    loadProfile();
    return () => { if (abortControllerRef.current) abortControllerRef.current.abort(); };
  }, [isAuthenticated]);

  const loadProfile = async () => {
    hasFetchedRef.current = true;
    abortControllerRef.current = new AbortController();
    try {
      setLoading(true); setError(""); setSuccess("");
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (!token) { navigate(ROUTES.LOGIN, { replace: true }); return; }
      const resp = await api.get(API_ENDPOINTS.AUTH.PROFILE, { signal: abortControllerRef.current.signal });
      const raw = resp?.data?.data ?? null;
      console.log("PROFILE resp =", resp);
      const user = normalizeUser(raw);
      if (!user?.id) throw new Error(ERROR_MESSAGES.PROFILE_SERVICE_UNAVAILABLE);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      setProfileData(user);
      setFormData({ name: user.name || "", address: user.address || "", alternateNumber: user.alternateNumber || "", profile: user.profile || "" });
      setPreviewImage(user.profile || null);
      if (updateUserProfile) updateUserProfile(user);
      setSuccess(SUCCESS_MESSAGES.PROFILE_LOADED);
    } catch (err) {
      if (err?.name === "AbortError" || err?.code === "ERR_CANCELED") return;
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || ERROR_MESSAGES.UNKNOWN_ERROR;
      if (status === 401 || status === 404) {
        localStorage.removeItem(STORAGE_KEYS.TOKEN); localStorage.removeItem(STORAGE_KEYS.USER);
        showNotification(ERROR_MESSAGES.SESSION_EXPIRED, NOTIFICATIONTYPES.ERROR);
        setTimeout(() => navigate(ROUTES.LOGIN, { replace: true }), 1200); return;
      }
      setProfileData(null); setError(msg);
      showNotification(msg, NOTIFICATIONTYPES.ERROR);
    } finally { setLoading(false); }
  };

  const validateField = (fieldName, value) => {
    const errors = { ...validationErrors };
    switch (fieldName) {
      case "name":
        if (!value?.trim()) errors.name = ERROR_MESSAGES.REQUIRED_FIELD;
        else if (value.trim().length < VALIDATION.NAME_MIN_LENGTH) errors.name = ERROR_MESSAGES.NAME_TOO_SHORT;
        else if (value.trim().length > VALIDATION.NAME_MAX_LENGTH) errors.name = ERROR_MESSAGES.NAME_TOO_LONG;
        else if (!VALIDATION.NAME_REGEX.test(value.trim())) errors.name = ERROR_MESSAGES.INVALID_NAME;
        else delete errors.name;
        break;
      case "alternateNumber":
        if (value?.trim() && !VALIDATION.PHONE_REGEX.test(value.trim())) errors.alternateNumber = ERROR_MESSAGES.INVALID_PHONE;
        else delete errors.alternateNumber;
        break;
      default: break;
    }
    setValidationErrors(errors);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
    if (error) setError("");
    if (success) setSuccess("");
  };

  const handleCancelEdit = () => {
    if (!profileData) return;
    setFormData({ name: profileData.name || "", address: profileData.address || "", alternateNumber: profileData.alternateNumber || "", profile: profileData.profile || "" });
    setPreviewImage(profileData.profile || null);
    setValidationErrors({}); setError(""); setSuccess(""); setIsEditing(false);
  };

  const handleSaveProfile = async () => {
    const errors = {};
    if (!formData.name?.trim()) errors.name = ERROR_MESSAGES.REQUIRED_FIELD;
    if (Object.keys(errors).length > 0 || Object.keys(validationErrors).length > 0) {
      setValidationErrors((prev) => ({ ...prev, ...errors }));
      setError(ERROR_MESSAGES.VALIDATION_ERROR); return;
    }
    try {
      setSaving(true); setError(""); setSuccess("");
      const payload = { name: formData.name?.trim() || null, address: formData.address?.trim() || null, alternateNumber: formData.alternateNumber?.trim() || null };
      const resp = await api.put(API_ENDPOINTS.AUTH.UPDATE_PROFILE, payload);
      console.log("PROFILE resp =", resp);
      const raw = resp?.data?.data ?? null;
      const updated = normalizeUser(raw);
      if (!updated?.id) throw new Error(ERROR_MESSAGES.SERVER_ERROR);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updated));
      setProfileData(updated); setPreviewImage(updated.profile || null);
      setFormData({ name: updated.name || "", address: updated.address || "", alternateNumber: updated.alternateNumber || "", profile: updated.profile || "" });
      setIsEditing(false); setSuccess(SUCCESS_MESSAGES.PROFILE_UPDATED);
      showNotification(SUCCESS_MESSAGES.PROFILE_UPDATED, NOTIFICATIONTYPES.SUCCESS);
      if (updateUserProfile) updateUserProfile(updated);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || ERROR_MESSAGES.SERVER_ERROR;
      setError(msg); showNotification(msg, NOTIFICATIONTYPES.ERROR);
    } finally { setSaving(false); }
  };

  const handleImageClick = () => setShowImageOptions(true);

  const handleImageOptionSelect = (type) => {
    setShowImageOptions(false);
    if (!fileInputRef.current) return;
    if (type === "camera") fileInputRef.current.setAttribute("capture", "environment");
    else fileInputRef.current.removeAttribute("capture");
    fileInputRef.current.click();
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!APP_CONFIG.ALLOWED_IMAGE_TYPES.includes(file.type)) {
      showNotification(ERROR_MESSAGES.INVALID_IMAGE_FORMAT, NOTIFICATIONTYPES.ERROR); return;
    }
    try {
      setUploadingImage(true);
      const compressedFile = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1024, useWebWorker: true, fileType: "image/jpeg" });
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(compressedFile);
      const fd = new FormData();
      fd.append("profile", compressedFile, "profile.jpg");
      const uploadResp = await api.post(API_ENDPOINTS.UPLOAD.PROFILEIMAGE, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: APP_CONFIG.FILE_UPLOAD_TIMEOUT });
      const imageUrl = uploadResp?.data?.profileImageUrl || uploadResp?.data?.data?.profile || uploadResp?.data?.data?.url || uploadResp?.data?.url || null;
      if (!imageUrl) throw new Error(ERROR_MESSAGES.UPLOAD_FAILED);
      setFormData((prev) => ({ ...prev, profile: imageUrl }));
      showNotification(SUCCESS_MESSAGES.AVATAR_UPDATED, NOTIFICATIONTYPES.SUCCESS);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || ERROR_MESSAGES.UPLOAD_FAILED;
      showNotification(msg, NOTIFICATIONTYPES.ERROR);
      setPreviewImage(profileData?.profile || null);
    } finally { setUploadingImage(false); e.target.value = ""; }
  };

  const inputClass = (hasError) => `w-full pl-11 pr-4 py-3 text-sm border rounded-xl outline-none transition-all
    ${isEditing
      ? `bg-white/[0.04] border-white/10 text-white placeholder:text-gray-600 focus:border-[#FF6B35]/50 focus:bg-white/[0.06] ${hasError ? "!border-red-500/50" : ""}`
      : "bg-white/[0.02] border-white/[0.04] text-gray-400 cursor-not-allowed"}`;

  // ── Loading State ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 border-2 border-[#FF6B35]/20 border-t-[#FF6B35] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center bg-[#111111] border border-white/[0.08] rounded-2xl p-10 max-w-sm w-full">
          <div className="w-14 h-14 bg-amber-400/10 border border-amber-400/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="h-7 w-7 text-amber-400" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Unable to Load Profile</h3>
          <p className="text-sm text-gray-500 mb-6">Please try again.</p>
          <button onClick={() => { hasFetchedRef.current = false; loadProfile(); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF6B35] text-white text-sm font-semibold rounded-xl hover:bg-[#E85520] transition-colors shadow-lg shadow-[#FF6B35]/20">
            <RefreshCw className="h-4 w-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const initials = (profileData.name || "U").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  // ── Main Render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0A0A0A]">

      {/* Cover + Header */}
      <div className="relative">
        {/* Cover image area */}
        <div className="h-40 sm:h-52 relative overflow-hidden">
          <div className="absolute inset-0 bg-[#111111]" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`,
              backgroundSize: "40px 40px",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]" />
          {/* Glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-[#FF6B35] rounded-full filter blur-[80px] opacity-10 pointer-events-none" />
        </div>

        {/* Profile card */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="-mt-20 sm:-mt-24 relative z-10 pb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-5">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                  className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl overflow-hidden border-4 border-[#0A0A0A] shadow-2xl bg-[#1A1A1A]">
                  {uploadingImage ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-[#1A1A1A] gap-2">
                      <Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" />
                      <p className="text-xs text-gray-500">Uploading...</p>
                    </div>
                  ) : previewImage ? (
                    <img src={previewImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FF6B35] to-[#E85520]">
                      <span className="text-3xl font-black text-white">{initials}</span>
                    </div>
                  )}
                </motion.div>
                <button type="button" onClick={handleImageClick} disabled={uploadingImage}
                  className="absolute -bottom-1 -right-1 w-9 h-9 bg-[#FF6B35] hover:bg-[#E85520] text-white rounded-xl shadow-lg shadow-[#FF6B35]/30 flex items-center justify-center transition-all disabled:opacity-50">
                  <Camera className="h-4 w-4" />
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
              </div>

              {/* Name + info + actions */}
              <div className="flex-1 min-w-0">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h1 className="text-2xl sm:text-3xl font-black text-white truncate">
                        {profileData.name || "Complete Your Profile"}
                      </h1>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        <span className="flex items-center gap-1.5 text-sm text-gray-500">
                          <Phone className="h-3.5 w-3.5 text-[#FF6B35]" />
                          +91 {profileData.phoneNumber || "—"}
                        </span>
                        {profileData.email && (
                          <span className="flex items-center gap-1.5 text-sm text-gray-500">
                            <Mail className="h-3.5 w-3.5 text-[#FF6B35]" />
                            <span className="truncate max-w-[180px]">{profileData.email}</span>
                          </span>
                        )}
                        {(profileData.isDocumentVerified === 1 || profileData.isDocumentVerified === true) && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-semibold bg-green-400/10 border border-green-400/20 text-green-400">
                            <ShieldCheck className="h-3.5 w-3.5" /> Verified
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 flex-shrink-0">
                      {!isEditing ? (
                        <motion.button type="button" onClick={() => setIsEditing(true)}
                          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#FF6B35] text-white text-sm font-bold rounded-xl hover:bg-[#E85520] transition-colors shadow-lg shadow-[#FF6B35]/20">
                          <Pencil className="h-4 w-4" /> Edit Profile
                        </motion.button>
                      ) : (
                        <>
                          <button type="button" onClick={handleCancelEdit} disabled={saving}
                            className="px-4 py-2.5 bg-white/[0.06] border border-white/10 text-gray-300 text-sm font-semibold rounded-xl hover:bg-white/10 transition-colors disabled:opacity-50">
                            Cancel
                          </button>
                          <motion.button type="button" onClick={handleSaveProfile}
                            disabled={saving || Object.keys(validationErrors).length > 0}
                            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white text-sm font-bold rounded-xl hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-500/20">
                            {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Saving...</> : <><Save className="h-4 w-4" />Save Changes</>}
                          </motion.button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-12">
        {/* Alerts */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="mb-5 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </motion.div>
          )}
          {success && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="mb-5 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-400">{success}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Personal Information */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-[#111111] border border-white/[0.08] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center">
                <CreditCard className="h-4 w-4 text-[#FF6B35]" />
              </div>
              <h2 className="text-base font-bold text-white">Personal Information</h2>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Full Name *</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                    disabled={!isEditing} placeholder="Enter your full name"
                    className={inputClass(validationErrors.name)} />
                </div>
                {validationErrors.name && (
                  <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />{validationErrors.name}
                  </p>
                )}
              </div>

              {/* Primary Phone (read-only) */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                  <input type="text" value={profileData.phoneNumber ? `+91 ${profileData.phoneNumber}` : "—"}
                    disabled
                    className="w-full pl-11 pr-4 py-3 text-sm border rounded-xl bg-white/[0.02] border-white/[0.04] text-gray-500 cursor-not-allowed outline-none" />
                </div>
              </div>

              {/* Alternate number */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Alternate Number</label>
                <div className="relative">
                  <Smartphone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                  <input type="tel" name="alternateNumber" value={formData.alternateNumber} onChange={handleInputChange}
                    disabled={!isEditing} placeholder="10 digit number" maxLength={10}
                    className={inputClass(validationErrors.alternateNumber)} />
                </div>
                {validationErrors.alternateNumber && (
                  <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />{validationErrors.alternateNumber}
                  </p>
                )}
              </div>

              {/* Address */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Address</label>
                <div className="relative">
                  <MapPin className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-600" />
                  <textarea name="address" value={formData.address} onChange={handleInputChange}
                    disabled={!isEditing} rows={3} placeholder="Enter your complete address"
                    className={`${inputClass(false)} resize-none`} />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Account Settings */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="bg-[#111111] border border-white/[0.08] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 bg-red-500/10 rounded-xl flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-red-400" />
              </div>
              <h2 className="text-base font-bold text-white">Account Settings</h2>
            </div>

            <p className="text-sm text-gray-500 mb-5 leading-relaxed">
              You can permanently delete your account. This action cannot be undone and all your data will be removed.
            </p>

            <div className="pt-5 border-t border-white/[0.06]">
              <DeleteAccountButton />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Image Options Modal */}
      <AnimatePresence>
        {showImageOptions && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setShowImageOptions(false); }}>
            <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }} transition={{ duration: 0.25 }}
              className="bg-[#111111] border border-white/[0.08] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl shadow-black/60">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-white">Choose Photo</h3>
                  <button type="button" onClick={() => setShowImageOptions(false)}
                    className="w-8 h-8 bg-white/[0.06] hover:bg-white/10 rounded-xl flex items-center justify-center text-gray-400 hover:text-white transition-all">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  <button type="button" onClick={() => handleImageOptionSelect("camera")}
                    className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-[#FF6B35] text-white rounded-xl font-bold hover:bg-[#E85520] transition-colors shadow-lg shadow-[#FF6B35]/20">
                    <Camera className="h-5 w-5" /> Take Photo
                  </button>
                  <button type="button" onClick={() => handleImageOptionSelect("gallery")}
                    className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-white/[0.06] border border-white/10 text-white rounded-xl font-bold hover:bg-white/10 transition-colors">
                    Choose from Gallery
                  </button>
                  <button type="button" onClick={() => setShowImageOptions(false)}
                    className="w-full px-6 py-3.5 text-gray-500 rounded-xl font-medium hover:text-gray-300 transition-colors text-sm">
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Profile;
